'use client';

import { Heading } from '@app/app/_components/Heading/Heading';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { Input } from '@app/app/_components/Input/Input';
import { TestChoiceButtonInput } from '@app/app/_components/Input/TestChoiceButtonInput';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { CommonType } from '@app/domain/model/CommonType';
import { SessionResponse } from '@app/domain/model/eshop/life/session/SessionResponse';

interface Props {
    errors?: ViolationMap;
    sessionData: Nullable<SessionResponse>;
}

export function HealthQuestionnaireStep({ errors, sessionData }: Props) {
    const t = useTranslations();

    const yesNoOptions = useMemo(
        () => [
            { value: CommonType.No, label: t('general.no') },
            { value: CommonType.Yes, label: t('general.yes') },
        ],
        [t],
    );

    return (
        <div className="flex w-full flex-col gap-6 max-sm:pb-20">
            <div className="flex flex-col gap-4 border-b border-neutral-300 pb-8">
                <Input
                    name="height"
                    label={t('eshop.life.myHeight')}
                    type="number"
                    placeholder="180 cm"
                    suffix="cm"
                    min={0}
                    error={errors?.height}
                    defaultValue={sessionData?.secondStep?.healthData?.height?.toString() ?? undefined}
                />

                <Input
                    name="weight"
                    label={t('eshop.life.myWeight')}
                    type="number"
                    placeholder="70 kg"
                    suffix="kg"
                    min={0}
                    error={errors?.weight}
                    defaultValue={sessionData?.secondStep?.healthData?.weight ?? undefined}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Heading level="h4">{t('eshop.life.workCapacityAndDisability')}</Heading>

                <Paragraph>{t('eshop.life.workCapacityAndDisabilityDescription')}</Paragraph>

                <TestChoiceButtonInput
                    name="workCapacity"
                    options={yesNoOptions}
                    error={errors?.workCapacity}
                    defaultChecked={sessionData?.secondStep?.healthData?.declaration ? CommonType.Yes : undefined}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Heading level="h4">{t('eshop.life.diseasesAndDisorders')}</Heading>

                <Paragraph>{t('eshop.life.diseasesAndDisordersDescription')}</Paragraph>

                <TestChoiceButtonInput
                    name="diseasesAndDisorders"
                    options={yesNoOptions}
                    error={errors?.diseasesAndDisorders}
                    defaultChecked={sessionData?.secondStep?.healthData?.declaration ? CommonType.No : undefined}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Heading level="h4">{t('eshop.life.surgicalProceduresAndExaminations')}</Heading>

                <Paragraph>{t('eshop.life.surgicalProceduresAndExaminationsDescription')}</Paragraph>

                <TestChoiceButtonInput
                    name="surgicalProcedures"
                    options={yesNoOptions}
                    error={errors?.surgicalProcedures}
                    defaultChecked={sessionData?.secondStep?.healthData?.declaration ? CommonType.No : undefined}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Heading level="h4">{t('eshop.life.injuries')}</Heading>

                <Paragraph>{t('eshop.life.injuriesDescription')}</Paragraph>

                <TestChoiceButtonInput
                    name="injuries"
                    options={yesNoOptions}
                    error={errors?.injuries}
                    defaultChecked={sessionData?.secondStep?.healthData?.declaration ? CommonType.No : undefined}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Heading level="h4">{t('eshop.life.prescriptionDrugs')}</Heading>

                <Paragraph>{t('eshop.life.prescriptionDrugsDescription')}</Paragraph>

                <TestChoiceButtonInput
                    name="prescriptionDrugs"
                    options={yesNoOptions}
                    error={errors?.prescriptionDrugs}
                    defaultChecked={sessionData?.secondStep?.healthData?.declaration ? CommonType.No : undefined}
                />
            </div>
        </div>
    );
}
